import { RouterLink } from '@redux-first-router-link';
import { format } from 'date-fns';
import {
  useCloudflareImage,
  getResponsiveImage,
} from '@/hooks/useCloudflareImage';
import { Row, Grid } from '@simplywallst/ui-core';
import { useTracking } from 'react-tracking';
import {
  ArticleHighlightTitle,
  ArticleHighlightWrapper,
  ArticleHighlightContent,
  AuthorPhoto,
  Byline,
  CoverImage,
  ExcerptContent,
  ExcerptHighlightWrapper,
} from './styled';
import { ROUTE_ARTICLE } from '@/constants/routes';
import type { Articles } from '@/hooks/useLatestArticles';

type Props = {
  article: Articles[number];
};

export const ArticleHighlight = ({ article }: Props) => {
  const { trackEvent } = useTracking();
  const fallbackImage =
    'https://simplywall.st/static/images/market-wrap-up-2.jpg';

  const coverImageSrc = useCloudflareImage({
    src: article.coverURL ?? fallbackImage,
    width: 575,
    height: 316,
    fit: 'cover',
  });

  const { srcSet: coverImageSrcSet } = getResponsiveImage({
    src: article.coverURL ?? fallbackImage,
    breakpoints: [
      { width: 575, height: 316, fit: 'cover' },
      { width: 375, height: 261, fit: 'cover' },
    ],
  });

  return (
    <ArticleHighlightWrapper>
      <Grid templateColumns="1fr" templateColumnsLg="1fr 1fr" gapMd="20px">
        <Grid>
          <RouterLink
            data-cy-id={`article-highlight-image-link`}
            to={{
              type: ROUTE_ARTICLE,
              payload: { slug: article.slug },
            }}
            inheritColor
            noUnderline={false}
            onClick={() =>
              trackEvent({
                type: 'track',
                action: 'click',
                subject: 'Link to article',
              })
            }
          >
            <CoverImage
              src={coverImageSrc}
              srcSet={coverImageSrcSet}
              width={575}
              height={316}
              alt="Article cover"
            ></CoverImage>
          </RouterLink>
        </Grid>
        <Grid>
          <ArticleHighlightContent>
            <RouterLink
              to={{
                type: ROUTE_ARTICLE,
                payload: { slug: article.slug },
              }}
              inheritColor
              noUnderline={true}
              onClick={() =>
                trackEvent({
                  type: 'track',
                  action: 'click',
                  subject: 'Link to article',
                })
              }
            >
              <ArticleHighlightTitle>{article.title}</ArticleHighlightTitle>
            </RouterLink>
            <Byline>
              {article.author && article.author.photoURL && (
                <AuthorPhoto>
                  <img
                    src={article.author.photoURL}
                    alt={article.author.name}
                  />
                </AuthorPhoto>
              )}
              {article.author?.slug && (
                <a
                  href={`https://simplywall.st/news/about-simply-wall-st-news/${article.author.slug}`}
                  data-cy-id={`article-highlight-author-link`}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => {
                    trackEvent({
                      type: 'track',
                      action: 'click',
                      subject: 'authorNameBelowArticle',
                    });
                  }}
                >
                  <span>{article.author?.name ?? 'Simply Wall St'}</span>
                </a>
              )}
              <time
                dateTime={format(new Date(article.created), 'MMMM dd, yyyy')}
              >
                {format(new Date(article.created), 'MMMM dd, yyyy')}
              </time>
            </Byline>
            <Row>
              <ExcerptHighlightWrapper>
                <ExcerptContent data-cy-id={`article-highlight-excerpt`}>
                  {article.excerpt}
                </ExcerptContent>
              </ExcerptHighlightWrapper>
            </Row>
          </ArticleHighlightContent>
        </Grid>
      </Grid>
    </ArticleHighlightWrapper>
  );
};
